import type { CSSProperties } from "react";

/**
 * The CafonaPay logo, option C (see brand/logo-options/README.md).
 *
 * The C of the mark is also the C of "CafonaPay": one continuous stroke on the circle the
 * original beads sat on. It starts gold (the pot) at the top right and fades to the ink colour
 * as it closes, thinning as it goes. scripts/brand.mjs draws the same stroke for the static
 * files; keep these constants in step with it.
 *
 * Here the C is a conic gradient cut out by the stroke's shape (a CSS mask), so the colour
 * runs smoothly along the curve and the end colour can follow the light or dark theme.
 */

const GOLD = "#F2B33D";
const SEGMENTS = 24;
/** Stroke width at the gold start and at the end (option C: thick, legible at nav size). */
const W0 = 12;
const W1 = 7;
/** Tight box around the stroke, so the C can sit in a line of text like a letter. */
const CROP = { x: 4.97, y: 4.14, w: 49.68, h: 54.06 };

/** t = 0 at the gold start (top right, 318° in SVG terms), t = 1 at the end (bottom right, 48°). */
function point(t: number): [number, number] {
  const a = ((318 - 270 * t) * Math.PI) / 180;
  return [32 + 22 * Math.cos(a), 32 + 22 * Math.sin(a)];
}

/** The stroke's shape: short round-capped arcs that thin from W0 to W1. Used only as a mask. */
function shape(viewBox: string): string {
  let paths = "";
  for (let i = 0; i < SEGMENTS; i++) {
    const t0 = i / SEGMENTS;
    const t1 = (i + 1) / SEGMENTS;
    const [x0, y0] = point(t0);
    const [x1, y1] = point(t1);
    const w = W0 + ((W1 - W0) * (t0 + t1)) / 2;
    paths += `<path d='M${x0.toFixed(2)} ${y0.toFixed(2)}A22 22 0 0 0 ${x1.toFixed(2)} ${y1.toFixed(2)}' stroke-width='${w.toFixed(2)}'/>`;
  }
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='${viewBox}'><g fill='none' stroke='black' stroke-linecap='round'>${paths}</g></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

const MASK_SQUARE = shape("0 0 64 64");
const MASK_CROP = shape(`${CROP.x} ${CROP.y} ${CROP.w} ${CROP.h}`);

/**
 * Conic gradient around the circle's centre. CSS angles start at 12 o'clock and run clockwise,
 * so the end of the stroke (48° in SVG terms) is at 138°; gold is reached 270° later, at the start.
 * The gap between them is split so each round cap keeps its own colour.
 */
function paint(end: string, centre: string, mask: string): CSSProperties {
  return {
    display: "inline-block",
    background: `conic-gradient(from 138deg at ${centre}, ${end} 0deg, ${GOLD} 270deg, ${GOLD} 315deg, ${end} 315deg)`,
    WebkitMaskImage: mask,
    maskImage: mask,
    WebkitMaskSize: "100% 100%",
    maskSize: "100% 100%",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
  };
}

const endColour = (light: boolean) => (light ? "#FFFFFF" : "var(--brand)");

/** The mark on its own (app icon, card, empty states). `light` is for dark backgrounds. */
export function LogoMark({ size = 32, light = false }: { size?: number; light?: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{ ...paint(endColour(light), "50% 50%", MASK_SQUARE), width: size, height: size, verticalAlign: "middle", flex: "none" }}
    />
  );
}

/** The wordmark: the stroke stands in for the C, at cap height, followed by "afonaPay". */
export function Logo({ size = 30, light = false }: { size?: number; light?: boolean }) {
  const capHeight = 0.69; // Source Serif 4 Bold, in em
  const centre = `${(((32 - CROP.x) / CROP.w) * 100).toFixed(2)}% ${(((32 - CROP.y) / CROP.h) * 100).toFixed(2)}%`;
  return (
    <span
      role="img"
      aria-label="CafonaPay"
      style={{
        display: "inline-block",
        fontFamily: "var(--font-display)",
        fontWeight: 700,
        fontSize: size * 0.72,
        lineHeight: 1.1,
        letterSpacing: "-0.03em",
        whiteSpace: "nowrap",
        color: light ? "#fff" : "var(--ink)",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          ...paint(endColour(light), centre, MASK_CROP),
          verticalAlign: "baseline",
          height: `${capHeight}em`,
          width: `${((capHeight * CROP.w) / CROP.h).toFixed(3)}em`,
          marginBottom: "-0.012em",
          marginRight: "0.04em",
        }}
      />
      <span aria-hidden="true">afonaPay</span>
    </span>
  );
}
